import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

import { StudentApi, studentsApi } from "api/studentsApi";
import { useToaster } from "utils";
import { PaziaButton, PaziaFormButton, PaziaInput } from "components/atoms";
import { PaziaModal } from "components/molecules";

interface ChangeEmailModalProps {
  onCloseRequest: () => void;
  student: StudentApi | null;
  onSuccess: (newEmail: string) => void;
}

const FORM_NAMES = {
  EMAIL: "email",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.EMAIL]: yup.string().email("Podaj prawidłowy adres email").required("Email jest wymagany"),
  })
  .required();

type ChangeEmailFormValues = yup.InferType<typeof schema>;

export const ChangeEmailModal = ({ onCloseRequest, student, onSuccess }: ChangeEmailModalProps) => {
  const { showSuccess, showError } = useToaster();
  const methods = useForm<ChangeEmailFormValues>({
    resolver: yupResolver(schema),
  });
  const { handleSubmit, reset, setError } = methods;

  const onSubmit = async (data: ChangeEmailFormValues) => {
    try {
      await studentsApi.changeEmail(student!.id, { email: data.email });
      showSuccess("Email użytkownika został zmieniony");
      reset({});
      onSuccess(data.email);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        // InputErrorMessage only renders errors that have a type
        setError(FORM_NAMES.EMAIL, { type: "manual", message: "Ten adres email jest już zajęty przez innego użytkownika" });
      } else {
        showError("Wystąpił błąd podczas zmiany emaila");
      }
    }
  };

  return (
    <PaziaModal id="change-email-modal" onCloseRequest={onCloseRequest} isOpen={student != null}>
      {student && (
        <div className="flex flex-col gap-4 px-5 pb-6 pt-4">
          <div className="text-xl">
            <span className="mb-2 font-medium text-slate-800 dark:text-slate-100">
              Zmiana emaila — {student.firstName} {student.surname}
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Aktualny email: <strong>{student.email}</strong>
          </p>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <PaziaInput name={FORM_NAMES.EMAIL} label="Nowy adres email" required />
              <div className="flex justify-end gap-2">
                <PaziaButton btnType="button" title="Anuluj" type="tertiary" onClick={() => { reset({}); onCloseRequest(); }} />
                <PaziaFormButton title="Zmień email" />
              </div>
            </form>
          </FormProvider>
        </div>
      )}
    </PaziaModal>
  );
};
